/**
 * Un .docx chargé sur /apv-encode est converti en PDF DANS LE NAVIGATEUR avant
 * l'envoi (demande de Chady, 2026-11-04 : « si on charge un .docx, d'abord ça
 * le transforme en PDF et puis ça l'envoie »). Le serveur n'accepte que PDF,
 * JPEG et PNG, et Claude lit des PDF.
 *
 * Sans LibreOffice sur le VPS ni nouvelle dépendance : un .docx est un zip
 * (lu ici avec DecompressionStream), son word/document.xml est parcouru
 * (paragraphes, titres, listes numérotées, tableaux, gras / italique /
 * souligné, sauts de page) et pdfmake — déjà présent par vue-pdfmake, police
 * Roboto avec les diacritiques roumains — produit le PDF. Les images, en-têtes
 * et pieds de page ne sont pas repris : c'est le texte du contrat qui compte
 * (lecture par l'IA, archive lisible sur la réception).
 *
 * `docxToDocDefinition` est pur (testable sous Node avec xmldom :
 * scripts/test-docx-to-pdf.js) ; `docxToPdfBlob` ajoute pdfmake, chargé à la
 * demande (le chunk des polices pèse ~1 Mo, inutile tant qu'aucun Word n'arrive).
 */

/* global DecompressionStream */
const SIG_EOCD = 0x06054b50
const SIG_CEN = 0x02014b50
const SIG_LOC = 0x04034b50

/** Taille de l'écriture courante : les contrats sont denses, 10 pt reste lisible. */
const FONT = 10

/* ------------------------------------------------------------------- zip */

async function inflateRaw (bytes) {
  if (typeof DecompressionStream !== 'function') {
    throw new Error('This browser cannot convert Word files. Save the contract as PDF and upload the PDF.')
  }
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('deflate-raw'))
  return new Uint8Array(await new Response(stream).arrayBuffer())
}

/** Les fichiers d'un zip (nom → octets). Pas de ZIP64 : un .docx n'en a pas besoin. */
export async function readZip (buffer) {
  const u8 = new Uint8Array(buffer)
  const dv = new DataView(buffer)
  let eocd = -1
  for (let i = u8.length - 22; i >= Math.max(0, u8.length - 65557); i--) {
    if (dv.getUint32(i, true) === SIG_EOCD) { eocd = i; break }
  }
  if (eocd < 0) throw new Error('This is not a .docx file (no zip directory).')
  const count = dv.getUint16(eocd + 10, true)
  let p = dv.getUint32(eocd + 16, true)
  const files = new Map()
  const dec = new TextDecoder()
  for (let i = 0; i < count; i++) {
    if (dv.getUint32(p, true) !== SIG_CEN) throw new Error('Corrupt .docx file (zip entry).')
    const method = dv.getUint16(p + 10, true)
    const csize = dv.getUint32(p + 20, true)
    const n = dv.getUint16(p + 28, true)
    const e = dv.getUint16(p + 30, true)
    const c = dv.getUint16(p + 32, true)
    const loc = dv.getUint32(p + 42, true)
    const name = dec.decode(u8.subarray(p + 46, p + 46 + n))
    if (dv.getUint32(loc, true) !== SIG_LOC) throw new Error('Corrupt .docx file (local header).')
    const start = loc + 30 + dv.getUint16(loc + 26, true) + dv.getUint16(loc + 28, true)
    const data = u8.subarray(start, start + csize)
    if (method === 0) files.set(name, data)
    else if (method === 8) files.set(name, await inflateRaw(data))
    else throw new Error(`Unsupported zip compression (${method}) in this .docx file.`)
    p += 46 + n + e + c
  }
  return files
}

/* ------------------------------------------------------------------- xml */

const isEl = (n) => !!n && n.nodeType === 1
const children = (el) => Array.from((el && el.childNodes) || []).filter(isEl)
function child (el, name) {
  if (!el) return null
  return children(el).find((c) => c.localName === name) || null
}
const byName = (doc, name) => Array.from(doc.getElementsByTagNameNS('*', name))

/** L'attribut w:<name>, ou null s'il manque. */
function attr (el, name) {
  if (!el) return null
  const v = el.getAttribute(`w:${name}`)
  return v === null || v === undefined || v === '' ? null : String(v)
}

/** Un drapeau Word (<w:b/>, <w:b w:val="0"/>…). */
function on (el) {
  if (!el) return false
  const v = attr(el, 'val')
  return v === null || !['0', 'false', 'off'].includes(v.toLowerCase())
}

/* ------------------------------------------------------------ numérotation */

function parseNumbering (doc) {
  const abstracts = {}
  const nums = {}
  if (!doc) return { abstracts, nums }
  byName(doc, 'abstractNum').forEach((a) => {
    const lvls = {}
    children(a).filter((c) => c.localName === 'lvl').forEach((l) => {
      lvls[attr(l, 'ilvl') || '0'] = {
        fmt: attr(child(l, 'numFmt'), 'val') || 'decimal',
        text: attr(child(l, 'lvlText'), 'val') || '',
        start: Number(attr(child(l, 'start'), 'val') || 1)
      }
    })
    abstracts[attr(a, 'abstractNumId')] = lvls
  })
  byName(doc, 'num').forEach((n) => { nums[attr(n, 'numId')] = attr(child(n, 'abstractNumId'), 'val') })
  return { abstracts, nums }
}

function roman (n) {
  const t = [[1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'], [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'], [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I']]
  let out = ''
  t.forEach(([v, s]) => { while (n >= v) { out += s; n -= v } })
  return out
}

function fmtNum (n, fmt) {
  if (fmt === 'lowerLetter') return String.fromCharCode(97 + ((n - 1) % 26))
  if (fmt === 'upperLetter') return String.fromCharCode(65 + ((n - 1) % 26))
  if (fmt === 'lowerRoman') return roman(n).toLowerCase()
  if (fmt === 'upperRoman') return roman(n)
  return String(n)
}

/** Les compteurs de listes : « 1. », « 1.2. », « a) », « • », comme Word les affiche. */
function makeNumberer (numbering) {
  const counters = {}
  return (numId, ilvl) => {
    const lvls = numbering.abstracts[numbering.nums[numId]]
    if (!lvls) return ''
    const L = Number(ilvl) || 0
    const c = counters[numId] || (counters[numId] = [])
    for (let i = 0; i <= L; i++) if (c[i] === undefined) c[i] = (lvls[i] ? lvls[i].start : 1) - 1
    c[L] += 1
    c.length = L + 1
    const lvl = lvls[L] || { fmt: 'decimal', text: `%${L + 1}.` }
    if (lvl.fmt === 'bullet') return '•'
    if (lvl.fmt === 'none') return ''
    return lvl.text.replace(/%(\d)/g, (m, d) => fmtNum(c[d - 1] === undefined ? 1 : c[d - 1], (lvls[d - 1] || lvl).fmt))
  }
}

/* --------------------------------------------------------------- blocs */

function runProps (rPr, base) {
  const p = Object.assign({}, base)
  if (!rPr) return p
  if (child(rPr, 'b')) p.bold = on(child(rPr, 'b'))
  if (child(rPr, 'i')) p.italics = on(child(rPr, 'i'))
  const u = child(rPr, 'u')
  if (u) p.underline = (attr(u, 'val') || 'single') !== 'none'
  const sz = child(rPr, 'sz')
  if (sz && attr(sz, 'val')) p.size = Number(attr(sz, 'val')) / 2
  return p
}

/**
 * Les morceaux de texte d'un paragraphe, dans l'ordre du document, à travers
 * liens, champs, contrôles de contenu et insertions suivies ; jamais le texte
 * supprimé (w:del), ni les images.
 */
function collectRuns (el, props, out) {
  children(el).forEach((c) => {
    switch (c.localName) {
    case 'r': {
      const rp = runProps(child(c, 'rPr'), props)
      children(c).forEach((x) => {
        if (x.localName === 't') out.push(Object.assign({ text: x.textContent || '' }, rp))
        else if (x.localName === 'tab') out.push(Object.assign({ text: '    ' }, rp))
        else if (x.localName === 'br') out.push(Object.assign({ text: '\n', page: attr(x, 'type') === 'page' }, rp))
        else if (x.localName === 'drawing' || x.localName === 'pict' || x.localName === 'object') out.images = (out.images || 0) + 1
      })
      break
    }
    case 'del': case 'moveFrom': case 'pPr': case 'rPr':
    case 'bookmarkStart': case 'bookmarkEnd': case 'proofErr':
    case 'commentRangeStart': case 'commentRangeEnd': case 'commentReference':
      break
    default:
      collectRuns(c, props, out)
    }
  })
}

function paragraph (p, ctx) {
  const pPr = child(p, 'pPr')
  const numPr = pPr ? child(pPr, 'numPr') : null
  const runs = []
  collectRuns(p, {}, runs)
  if (runs.images) ctx.images += runs.images
  return {
    type: 'p',
    runs,
    style: pPr ? attr(child(pPr, 'pStyle'), 'val') || '' : '',
    align: pPr ? attr(child(pPr, 'jc'), 'val') : null,
    num: numPr ? ctx.number(attr(child(numPr, 'numId'), 'val'), attr(child(numPr, 'ilvl'), 'val') || '0') : '',
    ilvl: numPr ? Number(attr(child(numPr, 'ilvl'), 'val') || 0) : 0,
    pageBefore: !!(pPr && child(pPr, 'pageBreakBefore') && on(child(pPr, 'pageBreakBefore')))
  }
}

function table (tbl, ctx) {
  const rows = []
  children(tbl).filter((c) => c.localName === 'tr').forEach((tr) => {
    const cells = []
    children(tr).filter((c) => c.localName === 'tc').forEach((tc) => {
      const tcPr = child(tc, 'tcPr')
      const span = Math.max(1, Number(tcPr ? attr(child(tcPr, 'gridSpan'), 'val') || 1 : 1))
      const vm = tcPr ? child(tcPr, 'vMerge') : null
      // Cellule fusionnée verticalement (suite) : vide, comme Word l'affiche.
      const merged = !!vm && attr(vm, 'val') !== 'restart'
      cells.push({ blocks: merged ? [] : walkBlocks(tc, ctx), span }) // eslint-disable-line no-use-before-define
    })
    if (cells.length) rows.push(cells)
  })
  return { type: 'table', rows }
}

function walkBlocks (container, ctx) {
  const out = []
  children(container).forEach((c) => {
    if (c.localName === 'p') out.push(paragraph(c, ctx))
    else if (c.localName === 'tbl') out.push(table(c, ctx))
    else if (c.localName === 'sdt') {
      const sc = child(c, 'sdtContent')
      if (sc) out.push(...walkBlocks(sc, ctx))
    } else if (['customXml', 'ins', 'moveTo', 'smartTag'].includes(c.localName)) out.push(...walkBlocks(c, ctx))
  })
  return out
}

/* ------------------------------------------------------------- pdfmake */

const ALIGN = { center: 'center', right: 'right', end: 'right', both: 'justify', distribute: 'justify' }

function runsToText (runs) {
  const out = runs.map((r) => {
    const t = { text: r.text }
    if (r.bold) t.bold = true
    if (r.italics) t.italics = true
    if (r.underline) t.decoration = 'underline'
    if (r.size && r.size >= 6 && r.size <= 40) t.fontSize = r.size
    return t
  }).filter((t) => t.text !== '')
  return out.length ? out : [{ text: ' ' }]
}

function paraToPdf (b, inCell) {
  const h = /^Heading(\d)/i.exec(b.style || '')
  const isTitle = /^Title$/i.test(b.style || '')
  let fontSize = inCell ? FONT - 1 : FONT
  let bold = false
  let top = inCell ? 1 : 2
  if (isTitle) { fontSize = 15; bold = true; top = 8 } else if (h) { fontSize = Math.max(11, 14 - Number(h[1])); bold = true; top = 8 }
  const runs = b.runs.filter((r) => !(r.page && r.text === '\n'))
  const text = runsToText(runs)
  const node = {
    text: b.num ? [{ text: `${b.num}  ` }].concat(text) : text,
    fontSize,
    margin: [b.ilvl * (inCell ? 8 : 14), top, 0, inCell ? 1 : 2]
  }
  if (bold) node.bold = true
  if (b.align && ALIGN[b.align]) node.alignment = ALIGN[b.align]
  const first = b.runs[0]
  const last = b.runs[b.runs.length - 1]
  if (b.pageBefore || (first && first.page)) node.pageBreak = 'before'
  else if (last && last.page && b.runs.length > 1) node.pageBreak = 'after'
  return node
}

function tableToPdf (t) {
  const rows = t.rows.filter((r) => r.length)
  if (!rows.length) return null
  const ncols = Math.max(1, ...rows.map((r) => r.reduce((n, c) => n + c.span, 0)))
  const body = rows.map((r) => {
    const row = []
    r.forEach((c) => {
      const stack = c.blocks.map((b) => blockToPdf(b, true)).filter(Boolean) // eslint-disable-line no-use-before-define
      const cell = { stack: stack.length ? stack : [{ text: ' ' }] }
      if (c.span > 1) cell.colSpan = c.span
      row.push(cell)
      for (let i = 1; i < c.span; i++) row.push({})
    })
    while (row.length < ncols) row.push({ text: ' ' })
    return row.slice(0, ncols)
  })
  return {
    table: { widths: Array(ncols).fill('*'), body },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      hLineColor: () => '#9a9a9a',
      vLineColor: () => '#9a9a9a',
      paddingTop: () => 3,
      paddingBottom: () => 3
    },
    margin: [0, 4, 0, 6]
  }
}

function blockToPdf (b, inCell) {
  return b.type === 'table' ? tableToPdf(b) : paraToPdf(b, inCell)
}

/* ------------------------------------------------------------ entrées */

/**
 * Le document pdfmake d'un .docx (ArrayBuffer). `parseXml(text)` rend un
 * Document DOM : DOMParser dans le navigateur, xmldom dans les tests.
 * Rend { docDefinition, stats: { paragraphs, tables, images, chars } }.
 */
export async function docxToDocDefinition (buffer, { parseXml, name }) {
  const files = await readZip(buffer)
  let mainName = 'word/document.xml'
  if (!files.has(mainName)) mainName = Array.from(files.keys()).find((k) => /(^|\/)document\.xml$/.test(k))
  if (!mainName) throw new Error('This is not a Word document (.docx): no document.xml inside.')
  const dec = new TextDecoder()
  const doc = parseXml(dec.decode(files.get(mainName)))
  if (!doc || doc.getElementsByTagName('parsererror').length) throw new Error('The Word document could not be read (broken XML).')
  const numDoc = files.has('word/numbering.xml') ? parseXml(dec.decode(files.get('word/numbering.xml'))) : null
  const body = byName(doc, 'body')[0]
  if (!body) throw new Error('The Word document has no body.')

  const ctx = { number: makeNumberer(parseNumbering(numDoc)), images: 0 }
  const blocks = walkBlocks(body, ctx)
  const content = blocks.map((b) => blockToPdf(b, false)).filter(Boolean)
  const chars = blocks.reduce(function count (n, b) {
    if (b.type === 'table') return b.rows.reduce((m, r) => r.reduce((k, c) => c.blocks.reduce(count, k), m), n)
    return n + b.runs.reduce((m, r) => m + r.text.trim().length, 0)
  }, 0)
  if (!chars) throw new Error('No text found in this Word file (scanned pages?). Save it as PDF and upload the PDF.')

  const title = String(name || 'document').replace(/\.docx?$/i, '')
  return {
    docDefinition: {
      info: { title },
      pageSize: 'A4',
      pageMargins: [48, 48, 48, 56],
      defaultStyle: { font: 'Roboto', fontSize: FONT, lineHeight: 1.15 },
      footer: (current, total) => ({
        text: `${title} — ${current} / ${total}`,
        alignment: 'center',
        fontSize: 7.5,
        color: '#777777',
        margin: [48, 20, 48, 0]
      }),
      content
    },
    stats: {
      paragraphs: blocks.filter((b) => b.type === 'p').length,
      tables: blocks.filter((b) => b.type === 'table').length,
      images: ctx.images,
      chars
    }
  }
}

/** Un fichier .docx (File) → PDF (Blob), dans le navigateur. */
export async function docxToPdfBlob (file) {
  const { docDefinition } = await docxToDocDefinition(await file.arrayBuffer(), {
    parseXml: (text) => new DOMParser().parseFromString(text, 'application/xml'),
    name: file.name
  })
  const [pdfMod, fontMod] = await Promise.all([
    import(/* webpackChunkName: "pdfmake" */ 'pdfmake/build/pdfmake'),
    import(/* webpackChunkName: "pdfmake-fonts" */ 'pdfmake/build/vfs_fonts')
  ])
  const pdfMake = pdfMod.default || pdfMod
  const vfs = (fontMod.pdfMake && fontMod.pdfMake.vfs) ||
    (fontMod.default && fontMod.default.pdfMake && fontMod.default.pdfMake.vfs) ||
    fontMod.vfs || (fontMod.default && fontMod.default.vfs)
  if (!vfs) throw new Error('PDF fonts are missing: the Word file cannot be converted.')
  pdfMake.vfs = vfs
  return new Promise((resolve, reject) => {
    try {
      pdfMake.createPdf(docDefinition).getBlob((blob) => resolve(blob))
    } catch (e) {
      reject(e)
    }
  })
}

/** Un .docx d'après son nom ou son type MIME. */
export function isDocx (file) {
  return /\.docx$/i.test(file.name || '') ||
    file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
}

/** Un .doc (ancien format binaire) : pas convertible ici. */
export function isLegacyDoc (file) {
  return /\.doc$/i.test(file.name || '') || file.type === 'application/msword'
}
